import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { uniqueId } from 'lodash'
import moment from 'moment'

import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  DialogTitle,
  Grid,
  TextField,
  MenuItem,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import clsx from 'clsx'
import { FormattedMessage } from 'react-intl'

import themesConfig from 'app/fuse-configs/themesConfig'
import {
  changeCSVData,
  setDialogEditCSVDataRow,
  useImportCSVData,
} from 'app/store/importCSV'
import { sendSingleRowToValidateThunk } from 'app/store/importCSV/importCSVExtraReducers'
import {
  STEP_PROGRESS_THREE,
  STEP_PROGRESS_TWO,
} from 'app/constants/subscriberCRM'
import { useDefaultLanguage } from 'commons/useDefaultLanguage'
import {
  HEADERS_IMPORT_CSV_FILE,
  dateColumns,
  keysToLowercase,
} from '../../constansts'
import AutocompleteJobTitle from '../EditableCell/AutocompleteJobTitle'
import { formattedDate } from '../../ImportFromCsv/StepOne/helpers'

import useStyles, { useStylesTextField } from './styles'

import ErrorIcon from 'app/components/icons/ErrorIcon'
import WarningIcon2 from 'app/components/icons/WarningIcon2'

const DialogEditCSVDataRow = () => {
  const dispatch = useDispatch()
  const [subscriberData, setSubscriberData] = useState({})
  const [disabledSaveBtn, setDisabledSaveBtn] = useState(false)

  const { dialogEditCSVData, optionsLanguages, activeStep } = useImportCSVData()

  const defaultLanguage = useDefaultLanguage()

  const { open, data, index } = dialogEditCSVData
  const classes = useStyles()

  const classesTextField = useStylesTextField()

  const handleClose = () => {
    const payload = { open: false }
    dispatch(setDialogEditCSVDataRow(payload))
  }

  const onChangeJobTitle = (value) => {
    setSubscriberData((prevState) => ({ ...prevState, job_role: value }))
  }

  const { errors: { invalidFields = {}, errorFields = [] } = {}, ...fields } =
    data || {}
  useEffect(() => {
    const initialData = { ...fields }

    keysToLowercase.forEach((key) => {
      if (initialData[key]) {
        initialData[key] = initialData[key].toLowerCase()
      }
    })

    setSubscriberData(initialData)
  }, [data])

  const onChange = (e) => {
    const { value, name } = e.target

    const getUpdatedValue = (value, name) => {
      if (dateColumns.includes(name)) {
        return value !== '' ? formattedDate(value, 'M/D/YYYY') : null
      }
      return value
    }

    setSubscriberData((prevState) => ({
      ...prevState,
      [name]: getUpdatedValue(value, name),
    }))
  }

  const getFieldLabel = (key) => {
    if (typeof key !== 'string' || key.length === 0) {
      return ''
    }
    if (key.includes('not_defined_header')) {
      return 'Not defined'
    }
    const label = key.replace(/[_-]/g, ' ')
    return label.charAt(0).toUpperCase() + label.slice(1)
  }

  const getInformationOnField = (key) => {
    return HEADERS_IMPORT_CSV_FILE.find(({ header }) => key === header) || {}
  }

  const getError = (key, subscriberKey) => {
    if (key === 'emailOrPhone') {
      if (errorFields.includes(subscriberKey)) {
        return invalidFields?.[key]
      }
    } else {
      return invalidFields?.[key]
    }
  }

  const saveChanges = async () => {
    const isUpdatedFromModal = true
    if (activeStep === STEP_PROGRESS_TWO) {
      dispatch(
        changeCSVData({
          changedSubscriber: subscriberData,
          index,
          isUpdatedFromModal,
        }),
      )
    } else if (activeStep === STEP_PROGRESS_THREE) {
      setDisabledSaveBtn(true)
      await dispatch(
        sendSingleRowToValidateThunk({
          changedSubscriber: subscriberData,
          index,
          isUpdatedFromModal,
        }),
      )
      setDisabledSaveBtn(false)
    }
    handleClose()
  }

  const fieldsElements = []

  const getLanguageOptions = (
    key,
    optionsLanguages,
    defaultLanguage,
    options,
  ) => {
    if (key === 'preferred_language') {
      return optionsLanguages.length
        ? optionsLanguages
        : [{ name: defaultLanguage.name, code: defaultLanguage.code }]
    }
    return options
  }

  const renderMenuItems = (options, headerKey) => {
    return options.map((option) => {
      const value = option?.name || option
      return (
        <MenuItem
          key={uniqueId()}
          value={
            keysToLowercase.includes(headerKey) ? value?.toLowerCase() : value
          }
        >
          {value}
        </MenuItem>
      )
    })
  }

  for (const key in subscriberData) {
    if (key === 'id') continue
    const value = subscriberData[key]
    const {
      required,
      options,
      errorKey,
      header: headerKey,
    } = getInformationOnField(key)
    const helperText = getError(errorKey, key) || getError(headerKey, key)

    let inputType = dateColumns.includes(key) ? 'date' : 'text'

    let currentValueInput = value

    if (inputType === 'date') {
      const formattedDateValue = formattedDate(value, 'YYYY-MM-DD')

      if (value !== null && !moment(value).isValid()) {
        inputType = 'text'
      } else {
        currentValueInput = formattedDateValue
      }
    }

    if (keysToLowercase.includes(headerKey)) {
      currentValueInput = currentValueInput?.toLowerCase()
    }

    let fieldItem = null
    const icon = required ? (
      <ErrorIcon
        classes="p-1 w-20"
        color={themesConfig.default.colors['red-500']}
      />
    ) : (
      <WarningIcon2 />
    )

    if (key === 'job_role') {
      fieldItem = (
        <AutocompleteJobTitle
          currentValueInput={currentValueInput}
          onChange={onChangeJobTitle}
          textFieldStyles={clsx(
            classesTextField.root,
            classesTextField[required ? 'error' : 'warning'],
          )}
          textFieldProps={{
            error: !!helperText,
            helperText: helperText,
            FormHelperTextProps: {
              component: (item) => {
                return (
                  <div className={clsx(item.className, 'flex items-start')}>
                    <div className="flex"> {icon}</div>
                    <span>{item.children}</span>
                  </div>
                )
              },
            },
          }}
        />
      )
    } else if (options) {
      fieldItem = (
        <TextField
          variant="outlined"
          name={key}
          value={value}
          error={!!helperText}
          helperText={helperText}
          FormHelperTextProps={{
            component: (item) => {
              return (
                <div className={clsx(item.className, 'flex items-start')}>
                  <div className="flex"> {icon}</div>
                  <span>{item.children}</span>
                </div>
              )
            },
          }}
          fullWidth
          select
          className={clsx(
            classesTextField.root,
            classesTextField[required ? 'error' : 'warning'],
          )}
          onChange={onChange}
          SelectProps={{
            IconComponent: () => null,
            MenuProps: {
              classes: { paper: classesTextField.select },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
              getContentAnchorEl: null,
            },
          }}
        >
          {renderMenuItems(
            getLanguageOptions(key, optionsLanguages, defaultLanguage, options),
            key,
          )}
        </TextField>
      )
    } else {
      fieldItem = (
        <TextField
          key={key}
          error={!!helperText}
          helperText={helperText}
          fullWidth
          type={inputType}
          name={key}
          value={currentValueInput}
          variant="outlined"
          FormHelperTextProps={{
            component: (item) => {
              return (
                <div className={clsx(item.className, 'flex items-start')}>
                  <div className="flex"> {icon}</div>
                  <span>{item.children}</span>
                </div>
              )
            },
          }}
          className={clsx(
            classesTextField.root,
            classesTextField[required ? 'error' : 'warning'],
          )}
          onChange={onChange}
        />
      )
    }
    fieldsElements.push(
      <Grid item xs={12} sm={6} key={key}>
        <Typography className={classes.fieldLabel}>
          {getFieldLabel(key)}
        </Typography>
        {fieldItem}
      </Grid>,
    )
  }

  return (
    <Dialog
      open={open}
      maxWidth={'md'}
      fullWidth={true}
      className={classes.dialogEditCSVData}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Typography variant="h6">
          <FormattedMessage id="subscribers.import.dialogEditCSVDataRow.title" />
        </Typography>
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          {fieldsElements}
        </Grid>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button
          className={clsx(classes.saveButton)}
          onClick={saveChanges}
          variant="contained"
          disabled={disabledSaveBtn}
          color="secondary"
        >
          <FormattedMessage id="action.save" />
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default DialogEditCSVDataRow
